// ── AR Flow — NOVA INTERFACE: Produtos ──────────────────────────────────────
// Busca de produtos por descrição, foto ou referência, com refinamento opcional.
// Fala só com o servidor do CRM (/api/produtos/*). Enquanto o catálogo não
// estiver conectado, mostra "em preparação" e não inventa nenhum produto.

import { useCallback, useEffect, useState } from "react";
import { AVISO_VALOR, ControlesBuscaCatalogo, pedidoCatalogo, pedidoSimilarCatalogo, ResultadosCatalogo } from "./CatalogoBusca";
import useCatalogStatus from "./useCatalogStatus";

const DISPLAY = "'Bricolage Grotesque', 'Source Sans 3', system-ui, sans-serif";

export const TIPOS = [
  ["", "Qualquer tipo"], ["abre_cama", "Abre cama"], ["sofa_cama", "Sofá-cama"], ["retratil", "Retrátil"],
  ["reclinavel", "Reclinável"], ["retratil_reclinavel", "Retrátil e reclinável"], ["fixo", "Fixo"], ["modular", "Modular"],
];
export const CATEGORIAS = [
  ["", "Todas"],
  // Sala
  ["sofa", "Sofás"], ["poltrona", "Poltronas"], ["puff", "Puffs"],
  ["mesa_centro", "Mesas de centro"], ["mesa_lateral", "Mesas laterais"], ["rack", "Racks"],
  ["estante", "Estantes"], ["banco", "Bancos"],
  // Jantar
  ["mesa_jantar", "Mesas de jantar"], ["cadeira", "Cadeiras"], ["banqueta", "Banquetas"],
  ["aparador", "Aparadores"], ["buffet", "Buffets"],
  // Quarto
  ["colchao", "Colchões"], ["cabeceira", "Cabeceiras"], ["base_box", "Bases / box"], ["box_bau", "Box baú"],
  ["cama_box", "Cama box (conjunto)"], ["cama", "Camas"], ["mesa_cabeceira", "Mesas de cabeceira"],
  ["roupeiro", "Roupeiros"], ["comoda", "Cômodas"], ["travesseiro", "Travesseiros"],
  // Demais categorias
  ["escrivaninha", "Escrivaninhas"], ["espelho", "Espelhos"], ["decoracao", "Decoração"],
  ["acessorio", "Acessórios"], ["area_externa", "Área externa"], ["cozinha", "Cozinha"], ["bar", "Bar"],
];
// Reduz a foto no próprio aparelho antes de enviar (até 1280 px, JPG).
export function reduzirFoto(arquivo, max = 1280) {
  return new Promise((resolve, reject) => {
    if (!arquivo || !/^image\//.test(arquivo.type)) { reject(new Error("Escolha um arquivo de imagem.")); return; }
    const leitor = new FileReader();
    leitor.onerror = () => reject(new Error("Não consegui ler a foto."));
    leitor.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Não consegui abrir a foto."));
      img.onload = () => {
        const escala = Math.min(1, max / Math.max(img.width, img.height));
        const c = document.createElement("canvas");
        c.width = Math.round(img.width * escala); c.height = Math.round(img.height * escala);
        c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
        resolve(c.toDataURL("image/jpeg", 0.82));
      };
      img.src = leitor.result;
    };
    leitor.readAsDataURL(arquivo);
  });
}

export async function lerJson(resposta) {
  const dados = await resposta.json().catch(() => ({}));
  if (!resposta.ok) { const e = new Error(dados.erro || dados.error || "Não foi possível concluir agora."); e.dados = dados; e.status = resposta.status; throw e; }
  return dados;
}

function textoSemValor(texto) {
  return String(texto || "").split(/\n|(?<=[.!?])\s+/)
    .filter(parte => !/(?:\bpre[cç]os?\b|\bvalores?\b|R\s*\$|\$\s*\d)/i.test(parte))
    .join(" ").trim();
}

// Aviso padrão enquanto o catálogo não está ligado.
export function EmPreparacao({ th, titulo, texto }) {
  const line = th.nuLine || th.sandDark;
  return (
    <div role="status" style={{ border: `1px dashed ${line}`, background: th.sandDark, borderRadius: 4, padding: "18px 20px", color: th.text }}>
      <div style={{ fontWeight: 700, fontSize: 15 }}>{titulo}</div>
      <div style={{ fontSize: 14, color: th.muted, marginTop: 4, lineHeight: 1.5 }}>{texto}</div>
    </div>
  );
}

function Ficha({ th, produto, onFechar }) {
  const line = th.nuLine || th.sandDark;
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState("");
  const produtoId = produto.isBiblioteca ? produto.produtoId : produto.id;
  useEffect(() => {
    if (!produtoId) { setDados({}); setErro(""); return undefined; }
    let vivo = true;
    fetch(`/api/produtos/item/${encodeURIComponent(produtoId)}`, { credentials: "include" })
      .then(lerJson).then(d => vivo && setDados(d.produto || d)).catch(e => vivo && setErro(e.message));
    return () => { vivo = false; };
  }, [produtoId]);
  const p = { ...produto, ...(dados || {}) };
  return (
    <div role="dialog" aria-modal="true" aria-label={p.nome} onClick={onFechar} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.35)", zIndex: 60, display: "flex", justifyContent: "flex-end" }}>
      <div onClick={e => e.stopPropagation()} style={{ width: "min(520px, 100%)", height: "100%", overflowY: "auto", background: "#fff", padding: "18px 20px 28px", boxSizing: "border-box", color: th.text }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 10 }}>
          <div>
            <div style={{ fontFamily: DISPLAY, fontSize: 22, fontWeight: 700 }}>{p.nome}</div>
            <div style={{ fontSize: 13, color: th.muted }}>{p.fornecedor || ""}</div>
          </div>
          <button type="button" onClick={onFechar} style={{ border: `1px solid ${line}`, background: "none", borderRadius: 4, padding: "6px 10px", cursor: "pointer", fontFamily: "inherit" }}>Fechar</button>
        </div>
        {(p.fotos || (p.fotoUrl ? [p.fotoUrl] : [])).slice(0, 6).map(u => (
          <img key={u} src={u} alt="" loading="lazy" style={{ width: "100%", marginTop: 12, borderRadius: 4, border: `1px solid ${line}` }} />
        ))}
        {erro && <div role="alert" style={{ marginTop: 12, color: "#991B1B", fontSize: 13 }}>{erro}</div>}
        {!dados && !erro && <div style={{ marginTop: 12, color: th.muted, fontSize: 13 }}>Carregando a ficha…</div>}
        <div style={{ marginTop: 12, color: th.muted, fontSize: 13 }}>{AVISO_VALOR}</div>
        {p.textoVenda && textoSemValor(p.textoVenda) && <p style={{ fontSize: 14, lineHeight: 1.5, marginTop: 14 }}>{textoSemValor(p.textoVenda)}</p>}
        {p.fichaTecnica && (
          <details style={{ marginTop: 14 }}>
            <summary style={{ cursor: "pointer", fontWeight: 700, fontSize: 14 }}>Ficha técnica</summary>
            <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap", marginTop: 6 }}>
              {typeof p.fichaTecnica === "string"
                ? textoSemValor(p.fichaTecnica)
                : Object.entries(p.fichaTecnica)
                  .filter(([key]) => !/(?:pre[cç]o|valor|tabela)/i.test(key))
                  .map(([key, value]) => `${key}: ${textoSemValor(value)}`).filter(Boolean).join("\n")}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}

// Lista de fornecedores do catálogo, para o filtro "Fornecedor".
export function useFornecedoresStatus() {
  const [status, setStatus] = useState({ fornecedores: [], carregando: true, erro: "", tentativa: 0 });
  const retry = useCallback(() => setStatus(atual => ({ ...atual, carregando: true, erro: "", tentativa: atual.tentativa + 1 })), []);
  useEffect(() => {
    let vivo = true;
    setStatus(atual => ({ ...atual, carregando: true, erro: "" }));
    fetch("/api/produtos/fornecedores", { credentials: "include" }).then(lerJson)
      .then(d => {
        if (!Array.isArray(d?.itens) || d.itens.some(f => !f || typeof f.nome !== "string" || !f.nome.trim())) {
          throw new Error("A resposta da lista de fornecedores é inválida.");
        }
        if (vivo) setStatus(atual => ({ ...atual, fornecedores: d.itens.map(f => f.nome.trim()), carregando: false, erro: "" }));
      })
      .catch(error => {
        if (vivo) setStatus(atual => ({ ...atual, carregando: false, erro: error.message || "Não foi possível carregar os fornecedores." }));
      });
    return () => { vivo = false; };
  }, [status.tentativa]);
  return { ...status, retry };
}

// Keep the original array-returning API for callers outside these screens.
export function useFornecedores() {
  return useFornecedoresStatus().fornecedores;
}

export default function Produtos({ theme }) {
  const th = theme;
  const line = th.nuLine || th.sandDark;
  const status = useCatalogStatus();
  const [textoBusca, setTextoBusca] = useState("");
  const [medida, setMedida] = useState("");
  const [fornecedor, setFornecedor] = useState("");
  const [foto, setFoto] = useState(null);
  const fornecedorStatus = useFornecedoresStatus();
  const fornecedores = fornecedorStatus.fornecedores;
  const [buscando, setBuscando] = useState(false);
  const [erro, setErro] = useState("");
  const [resultado, setResultado] = useState(null);
  const [aberto, setAberto] = useState(null);
  const enviarBusca = async pedido => {
    setBuscando(true); setErro(""); setResultado(null);
    try {
      const r = await fetch("/api/produtos/busca", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(pedido) });
      setResultado(await lerJson(r));
    } catch (err) { setErro(err.message); } finally { setBuscando(false); }
  };

  const buscar = () => {
    if (!textoBusca.trim() && !foto && !fornecedor) { setErro("Descreva o que procura ou escolha uma foto."); return; }
    return enviarBusca(pedidoCatalogo({ texto: textoBusca, fornecedor, medida, foto, limite: 30 }));
  };
  const buscarSimilar = produto => enviarBusca(pedidoSimilarCatalogo(produto, { fornecedor, medida, limite: 30 }));
  const escolherFoto = async (data, error) => {
    if (error) setErro(error.message);
    else { setFoto(data); setErro(""); }
  };
  const desligado = !status.conectado;

  return (
    <div>
      <div style={{ fontFamily: DISPLAY, fontSize: 26, fontWeight: 700, color: th.text }}>Produtos</div>
      <div style={{ fontSize: 14, color: th.muted, marginBottom: 8 }}>Descreva o produto. Se precisar, refine por fornecedor ou medida.</div>
      <div role="note" style={{ fontSize: 12, color: th.muted, margin: "0 0 12px" }}>{AVISO_VALOR}</div>

      {(status.state === "verificando" || status.state === "retentando") && (
        <div role="status" style={{ margin: "8px 0", color: th.muted, fontSize: 13 }}>
          {status.state === "retentando" ? "A verificação demorou ou falhou; tentando novamente…" : "Verificando a conexão do catálogo…"}
        </div>
      )}
      {status.state === "falha" && (
        <div role="alert" style={{ margin: "8px 0", color: "#991B1B", fontSize: 13 }}>
          Não foi possível verificar a conexão do catálogo: {status.error}
        </div>
      )}
      {status.state === "falha" && <button type="button" onClick={status.retry} style={{ margin: "8px 0", border: `1px solid ${line}`, background: "#fff", borderRadius: 4, padding: "7px 10px", cursor: "pointer" }}>Verificar catálogo novamente</button>}
      {status.conectado === false && (
        <div style={{ marginBottom: 14 }}>
          <EmPreparacao th={th} titulo="Busca de produtos em preparação"
            texto="O catálogo dos fornecedores está sendo carregado. Quando estiver conectado, a busca funciona aqui, sem precisar atualizar nada." />
        </div>
      )}
      {status.state === "desconectado" && <button type="button" onClick={status.retry} style={{ marginBottom: 10, border: `1px solid ${line}`, background: "#fff", borderRadius: 4, padding: "7px 10px", cursor: "pointer" }}>Verificar catálogo novamente</button>}

      {fornecedorStatus.carregando && <div aria-live="polite" style={{ marginBottom: 8, color: th.muted, fontSize: 13 }}>Carregando fornecedores…</div>}
      {fornecedorStatus.erro && (
        <div style={{ marginBottom: 8 }}>
          <div role="alert" style={{ color: "#991B1B", fontSize: 13 }}>Não foi possível carregar os fornecedores: {fornecedorStatus.erro}</div>
          <button type="button" onClick={fornecedorStatus.retry} style={{ marginTop: 5, border: `1px solid ${line}`, background: "#fff", borderRadius: 4, padding: "6px 9px", cursor: "pointer" }}>Tentar carregar fornecedores novamente</button>
        </div>
      )}

      <div style={{ background: "#fff", border: `1px solid ${line}`, borderRadius: 4, padding: 14 }}>
        <ControlesBuscaCatalogo texto={textoBusca} onTexto={setTextoBusca} fornecedores={fornecedores}
          fornecedor={fornecedor} onFornecedor={setFornecedor} medida={medida} onMedida={setMedida}
          onBuscar={buscar} buscando={buscando} desabilitado={desligado} foto={foto}
          onFoto={escolherFoto} onRemoverFoto={() => setFoto(null)} reduzirFoto={reduzirFoto} />
      </div>

      {erro && <div role="alert" style={{ marginTop: 12, background: "#FEF2F2", border: "1px solid #FCA5A5", color: "#991B1B", borderRadius: 4, padding: "9px 12px", fontSize: 14 }}>{erro}</div>}

      {resultado && (
        <ResultadosCatalogo theme={th} resposta={resultado} onDetalhes={setAberto} onSimilares={buscarSimilar} />
      )}

      {aberto && <Ficha th={th} produto={aberto} onFechar={() => setAberto(null)} />}
    </div>
  );
}
